/**
 * `<policy-editor-sqlaccess>` — the `SqlAccessPolicy` body (`SC-F511`, `SC-S509`).
 *
 * This is the access gate, and two semantics dominate everything an author does with
 * it. The editor carries them as a permanent explainer, taken verbatim from the form
 * schema's own `help` for the kind rather than paraphrased here:
 *
 *   - **Default-deny plus deny-override.** With no matching allow the request is
 *     denied and the engine records that it *abstained*, so an absent allow and an
 *     explicit deny look different in the audit trail and identical to the client.
 *     Deny wins over allow at any priority.
 *   - **An allow spans the statement.** A clause matches when the subject matches and
 *     *at least one* referenced table satisfies the resource half — so an allow on
 *     `orders` also admits a query joining `orders` with a table the policy never
 *     names. Only an explicit deny on the sensitive table blocks the join.
 *
 * `effect` is a radio with **no default selected**: it is always emitted, because a
 * reader of the YAML must never have to infer whether a policy grants or refuses.
 *
 * `errorCode` is a closed select over the 22-code catalogue. Sluice takes the value
 * verbatim and validates it neither at decode nor at compile, so a code outside the
 * catalogue falls through the status table to HTTP 500 with the internal-error
 * message — one typo turns a deliberate, explainable denial into what the client
 * reads as an outage, with nothing anywhere naming the policy.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "../../components/index.js";
import type { BnSelectChangeDetail } from "../../components/index.js";
import type { PolicyFinding } from "../../api/policies.js";
import type { BindingSummary } from "./bindings.js";
import { renderConditions } from "./kind-widgets.js";
import type { Selector, SqlAccessSpec } from "./model.js";
import { diagnosticSeverity, findingsAt, worst } from "./policy-findings.js";
import "./policy-selector.js";
import type { SelectorChangeDetail } from "./policy-selector.js";
import type { PolicySchema } from "./schema.js";
import type { SchemaCache } from "./schema-cache.js";

/** Detail of `spec-change`, shared by every kind editor. */
export interface SpecChangeDetail<T> {
  spec: T;
}

@customElement("policy-editor-sqlaccess")
export class PolicyEditorSqlAccess extends LitElement {
  @property({ attribute: false }) schema!: PolicySchema;
  @property({ attribute: false }) spec: SqlAccessSpec = { match: {} };
  @property({ attribute: false }) findings: PolicyFinding[] = [];
  @property({ attribute: false }) cache: SchemaCache = {
    tables: [],
    degraded: true,
    warning: "",
    fetchedAt: "",
  };
  @property({ attribute: false }) bindings: BindingSummary[] = [];
  @property({ attribute: false }) availableTags: string[] = [];

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    section {
      margin-bottom: var(--bino-space-xl);
    }
    h3 {
      margin: 0 0 var(--bino-space-xs);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .lede {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    fieldset {
      border: none;
      margin: 0 0 var(--bino-space-md);
      padding: 0;
    }
    legend {
      font-size: var(--bino-font-size-base);
      font-weight: 500;
      color: var(--bino-text-muted);
      padding: 0;
      margin-bottom: 0.375rem;
    }
    .effects {
      display: grid;
      gap: var(--bino-space-xs);
    }
    label.radio {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      cursor: pointer;
    }
    label.radio input {
      margin-top: 0.25rem;
      accent-color: var(--bino-primary);
    }
    label.radio input:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
      border-radius: var(--bino-radius-sm);
    }
    label.radio .desc {
      display: block;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    .row {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
    }
    .row > * {
      flex: 1 1 16rem;
      min-width: 0;
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .alert {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: var(--bino-space-xs);
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-sm);
      max-width: 80ch;
    }
    .alert.error {
      color: var(--bino-error);
    }
    .alert.warn {
      color: var(--bino-warning);
    }
    .rule {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      padding: 0 var(--bino-space-xs);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-inset);
    }
  `;

  /**
   * One finding that belongs to a group of controls rather than to a single input.
   *
   * It is a `role="alert"` paragraph and not an empty bn-field: a labelled field
   * wrapping nothing is announced as a form control with no purpose, which is worse
   * for a screen reader than a plain live region.
   */
  private renderAlert(finding: PolicyFinding) {
    return html`<p class="alert ${diagnosticSeverity(finding.severity) === "error" ? "error" : "warn"}" role="alert">
      <code class="rule">${finding.rule}</code> ${finding.message}
    </p>`;
  }

  private emit(patch: Partial<SqlAccessSpec>) {
    this.dispatchEvent(
      new CustomEvent<SpecChangeDetail<SqlAccessSpec>>("spec-change", {
        detail: { spec: { ...this.spec, ...patch } },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** The kind's permanent explainer, from the schema. */
  private renderExplainer() {
    const help = this.schema.kind("SqlAccessPolicy")?.help ?? "";
    if (!help) return nothing;
    return html`<bn-panel eyebrow="How this kind decides">
      <p class="lede">${help}</p>
    </bn-panel>`;
  }

  private renderEffect() {
    const field = this.schema.field("sqlAccessSpec", "effect");
    const options = this.schema.options(field?.enum ?? "Effect");
    const finding = worst(findingsAt(this.findings, "spec.effect"));
    return html`<fieldset>
      <legend>Effect</legend>
      <div class="effects">
        ${options.map(
          (o) => html`<label class="radio">
            <input
              type="radio"
              name="effect"
              .checked=${this.spec.effect === o.value}
              @change=${() => this.emit({ effect: o.value })}
            />
            <span>
              <strong>${o.label}</strong>
              ${o.description ? html`<span class="desc">${o.description}</span>` : nothing}
            </span>
          </label>`,
        )}
      </div>
      ${this.spec.effect
        ? nothing
        : html`<p class="alert error" role="alert">
            Choose allow or deny. There is no default: the value is always emitted, so a reader of the
            YAML never has to infer whether a policy grants or refuses.
          </p>`}
      ${finding ? this.renderAlert(finding) : nothing}
    </fieldset>`;
  }

  private renderMessageAndCode() {
    const messageField = this.schema.field("sqlAccessSpec", "message");
    const codeField = this.schema.field("sqlAccessSpec", "errorCode");
    const codeFinding = worst(findingsAt(this.findings, "spec.errorCode"));
    const codeDefault = String(codeField?.default ?? "");
    return html`<div class="row">
      <bn-field label="Client-visible message" hint=${messageField?.help ?? ""}>
        <bn-input
          .value=${this.spec.message ?? ""}
          placeholder=${String(messageField?.default ?? "")}
          @input=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.emit({ message: e.detail.value });
          }}
        ></bn-input>
      </bn-field>
      <bn-field
        label="Error code"
        hint=${codeField?.help ?? this.schema.enumHelp(codeField?.enum)}
        error=${codeFinding?.message ?? ""}
        rule=${codeFinding?.rule ?? ""}
        severity=${codeFinding ? diagnosticSeverity(codeFinding.severity) : "error"}
      >
        <bn-select
          filterable
          .options=${this.schema.options(codeField?.enum ?? "ErrorCode")}
          .value=${this.spec.errorCode ?? codeDefault}
          placeholder=${codeDefault}
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
            e.stopPropagation();
            this.emit({ errorCode: e.detail.value });
          }}
        ></bn-select>
      </bn-field>
    </div>`;
  }

  /**
   * The CEL condition editor (`SC-F510`), delegated to `<policy-conditions>` so
   * that all five kinds carrying `spec.conditions` render one editor.
   *
   * It is rendered unconditionally rather than only when a condition exists,
   * because on this kind the condition stage is where an author expresses the
   * narrowings the matcher cannot — a query-shape test, a claim comparison — and a
   * section that appeared only after an import would leave that unreachable from
   * the form.
   */
  private renderConditionsSection() {
    return html`<section>
      ${renderConditions(
        this.schema,
        this.spec.conditions,
        (conditions) => this.emit({ conditions }),
        this.findings,
      )}
    </section>`;
  }

  override render() {
    if (!this.schema) return nothing;
    const excludeScope = this.schema.kind("SqlAccessPolicy")?.excludeScope ?? "";
    const excludeHelp = this.schema.help("sqlAccessSpec", "exclude");
    return html`
      <section>${this.renderExplainer()}</section>

      <section>
        <h3>The rule itself</h3>
        ${this.renderEffect()} ${this.renderMessageAndCode()}
      </section>

      <section>
        <h3>Who and what it applies to</h3>
        <policy-selector
          .schema=${this.schema}
          .selector=${this.spec.match}
          path="spec.match"
          .cache=${this.cache}
          .findings=${this.findings}
          .bindings=${this.bindings}
          .availableTags=${this.availableTags}
          @selector-change=${(e: CustomEvent<SelectorChangeDetail>) => {
            e.stopPropagation();
            this.emit({ match: e.detail.selector });
          }}
        ></policy-selector>
      </section>

      <section>
        <h3>Exceptions</h3>
        <policy-selector
          is-exclude
          .schema=${this.schema}
          .selector=${this.spec.exclude ?? {}}
          path="spec.exclude"
          .scopeExplainer=${excludeHelp ||
          (excludeScope === "whole-query"
            ? "If this matches anywhere in the query, the entire policy is dropped for that query."
            : "")}
          .cache=${this.cache}
          .findings=${this.findings}
          .bindings=${this.bindings}
          .availableTags=${this.availableTags}
          @selector-change=${(e: CustomEvent<SelectorChangeDetail>) => {
            e.stopPropagation();
            const selector: Selector = e.detail.selector;
            this.emit({
              exclude:
                (selector.any?.length ?? 0) === 0 && (selector.all?.length ?? 0) === 0
                  ? undefined
                  : selector,
            });
          }}
        ></policy-selector>
      </section>

      ${this.renderConditionsSection()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "policy-editor-sqlaccess": PolicyEditorSqlAccess;
  }
}
